import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AUTH_STORAGE_KEY = 'aquila-admin-auth';

const createEmptyRow = () => ({
  clientId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: null,
  active: true,
  charter_company: '',
  aircraft_type: '',
  model: '',
  distance_km: '',
  seats: '',
  cruise_speed_kmh: '',
});

const withClientIds = (rows) =>
  rows.map((row) => ({
    ...row,
    clientId: row.id ? `aircraft-${row.id}` : `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  }));

const toApiRows = (rows) =>
  rows.map(({ clientId, ...row }) => ({
    ...row,
    charter_company: row.charter_company || null,
    aircraft_type: row.aircraft_type || null,
    model: row.model || null,
    distance_km: row.distance_km || null,
    seats: row.seats || null,
    cruise_speed_kmh: row.cruise_speed_kmh || null,
  }));

function AdminPage() {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [authHeader, setAuthHeader] = useState(() => sessionStorage.getItem(AUTH_STORAGE_KEY) || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const requestConfig = useMemo(
    () => ({
      headers: authHeader ? { Authorization: authHeader } : {},
    }),
    [authHeader]
  );

  const applySheetResponse = useCallback((data) => {
    setRows(withClientIds(data.rows || []));
    setMessage(`${data.active_count || 0} active aircraft, ${data.total_count || 0} total rows`);
  }, []);

  const loadSheet = useCallback(
    async (header = authHeader) => {
      if (!header) {
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await axios.get('/admin/aircraft/sheet', {
          headers: { Authorization: header },
        });
        applySheetResponse(response.data);
      } catch (loadError) {
        setError(loadError.response?.status === 401 ? 'Admin login failed.' : 'Unable to load the aircraft sheet.');
        if (loadError.response?.status === 401) {
          sessionStorage.removeItem(AUTH_STORAGE_KEY);
          setAuthHeader('');
        }
      } finally {
        setLoading(false);
      }
    },
    [applySheetResponse, authHeader]
  );

  useEffect(() => {
    if (authHeader) {
      void loadSheet(authHeader);
    }
  }, [authHeader, loadSheet]);

  const handleLogin = async (event) => {
    event.preventDefault();
    const nextAuthHeader = `Basic ${window.btoa(`${username}:${password}`)}`;

    setLoading(true);
    setError('');
    try {
      await axios.get('/admin/me', {
        headers: { Authorization: nextAuthHeader },
      });
      sessionStorage.setItem(AUTH_STORAGE_KEY, nextAuthHeader);
      setAuthHeader(nextAuthHeader);
      setPassword('');
    } catch {
      setError('Admin login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthHeader('');
    setRows([]);
    setMessage('');
  };

  const updateRow = (index, field, value) => {
    setRows((currentRows) =>
      currentRows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    setRows((currentRows) => [...currentRows, createEmptyRow()]);
  };

  const removeRow = (index) => {
    setRows((currentRows) => currentRows.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveRows = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(
        '/admin/aircraft/sheet',
        { rows: toApiRows(rows) },
        requestConfig
      );
      applySheetResponse(response.data);
      setMessage('Aircraft sheet saved.');
    } catch {
      setError('Unable to save the aircraft sheet.');
    } finally {
      setSaving(false);
    }
  };

  const importWorkbook = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      const workbookBytes = await file.arrayBuffer();
      const response = await axios.post('/admin/aircraft/import-xlsx', workbookBytes, {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      applySheetResponse(response.data);
      setMessage('Aircraft workbook imported.');
    } catch {
      setError('Unable to import the aircraft workbook.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inner-page admin-page">
      <section className="inner-page-hero">
        <p className="inner-page-eyebrow">Admin Portal</p>
        <h1>Aircraft Sheet</h1>
        <p>Import and edit the aircraft records used by the public aircraft guide.</p>
      </section>

      <section className="page-shell">
        {!authHeader && (
          <form className="panel-card admin-login" onSubmit={handleLogin}>
            <label className="form-field">
              <span>Username</span>
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
            </label>
            <label className="form-field">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </label>
            <button className="primary-button" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            {error && <p className="status-note status-note--error">{error}</p>}
          </form>
        )}

        {authHeader && (
          <div className="panel-card admin-sheet-panel">
            <div className="admin-toolbar">
              <div>
                <p className="status-note status-note--success">{message || 'Aircraft sheet loaded.'}</p>
                {error && <p className="status-note status-note--error">{error}</p>}
              </div>
              <div className="admin-actions">
                <button className="aircraft-action" type="button" onClick={() => loadSheet()} disabled={loading || saving}>
                  Reload
                </button>
                <label className="aircraft-action admin-file-action">
                  Import XLSX
                  <input type="file" accept=".xlsx" onChange={importWorkbook} disabled={saving} />
                </label>
                <button className="aircraft-action" type="button" onClick={addRow} disabled={saving}>
                  Add Row
                </button>
                <button className="primary-button" type="button" onClick={saveRows} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Sheet'}
                </button>
                <button className="aircraft-back-button" type="button" onClick={handleLogout}>
                  Sign out
                </button>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-sheet-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th>Charter Company</th>
                    <th>Type</th>
                    <th>Model</th>
                    <th>Distance(km)</th>
                    <th>Seats</th>
                    <th>Cruise speed(Km/h)</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.clientId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(row.active)}
                          onChange={(event) => updateRow(index, 'active', event.target.checked)}
                        />
                      </td>
                      <td>
                        <input
                          value={row.charter_company || ''}
                          onChange={(event) => updateRow(index, 'charter_company', event.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          value={row.aircraft_type || ''}
                          onChange={(event) => updateRow(index, 'aircraft_type', event.target.value)}
                          list="aircraft-type-options"
                        />
                      </td>
                      <td>
                        <input value={row.model || ''} onChange={(event) => updateRow(index, 'model', event.target.value)} />
                      </td>
                      <td>
                        <input
                          value={row.distance_km || ''}
                          onChange={(event) => updateRow(index, 'distance_km', event.target.value)}
                        />
                      </td>
                      <td>
                        <input value={row.seats || ''} onChange={(event) => updateRow(index, 'seats', event.target.value)} />
                      </td>
                      <td>
                        <input
                          value={row.cruise_speed_kmh || ''}
                          onChange={(event) => updateRow(index, 'cruise_speed_kmh', event.target.value)}
                        />
                      </td>
                      <td>
                        <button className="admin-remove-button" type="button" onClick={() => removeRow(index)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <datalist id="aircraft-type-options">
              <option value="Turboprop" />
              <option value="Light Jet" />
              <option value="Midsize Jet" />
              <option value="Heavy Jet" />
              <option value="Helicopter" />
              <option value="Piston" />
            </datalist>
          </div>
        )}
      </section>
    </div>
  );
}

export default AdminPage;
